import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { consultationService } from '../services/consultationApi';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 2000;

const JungPage: React.FC = () => {
  const [relato, setRelato] = useState('');
  const [pergunta, setPergunta] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!relato.trim() || isLoading) return;
    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const q = `${relato.trim()}${pergunta.trim() ? `\n\nPergunta específica: ${pergunta.trim()}` : ''}`;
      const result = await consultationService.askAurora(q, 'jung');
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Psicologia & Jung</h1>
        <div className="w-20" />
      </div>
      <AuroraLoadingOverlay isLoading={isLoading} message="Aurora está analisando..." />

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">🧠</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Psicologia & Jung</h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Compartilhe uma situação ou estado emocional. Aurora Lyz analisa através dos arquétipos junguianos — Sombra, Anima, Persona, Self — e aponta caminhos de integração.
          </p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por consulta</p>
        </div>

        <AnimatePresence mode="wait">
          {!answer && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>

              <div className="mb-1">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Seu relato</label>
              </div>
              <div className="relative mb-4">
                <textarea
                  value={relato}
                  onChange={e => setRelato(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Descreva a situação, sentimento ou padrão que quer compreender. Ex: Sinto que me saboto sempre que estou prestes a conquistar algo importante. Isso se repete há anos..."
                  rows={6}
                  className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                  style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                  disabled={isLoading}
                />
                <span className="absolute bottom-3 right-4 text-white/30 text-xs">{relato.length}/{MAX_CHARS}</span>
              </div>

              <div className="mb-1">
                <label className="text-white/60 text-xs font-medium uppercase tracking-wider">Pergunta específica <span className="normal-case text-white/35">(opcional)</span></label>
              </div>
              <input
                type="text"
                value={pergunta}
                onChange={e => setPergunta(e.target.value.slice(0, 300))}
                placeholder="Ex: Qual arquétipo está mais ativo em mim agora?"
                className="w-full border rounded-2xl px-5 py-3 focus:outline-none text-sm mb-4"
                style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.2)', color: 'white', caretColor: 'white' }}
                disabled={isLoading}
              />

              <NoTokensBanner error={error} />

              <motion.button
                onClick={handleSubmit}
                disabled={!relato.trim() || isLoading}
                whileHover={relato.trim() && !isLoading ? { scale: 1.03 } : {}}
                whileTap={relato.trim() && !isLoading ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #0f766e, #0d9488)' }}
              >
                {isLoading ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Aurora está analisando…</>
                ) : (
                  <><Sparkles size={18} />Analisar com Jung</>
                )}
              </motion.button>
            </motion.div>
          )}

          {answer && (
            <motion.div key="answer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
              <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-white/45 text-xs mb-1">Seu relato</p>
                <p className="text-white/75 text-sm italic line-clamp-3">"{relato}"</p>
              </div>
              <div className="rounded-2xl p-4 sm:p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(15,118,110,0.15) 0%, rgba(13,148,136,0.10) 100%)', border: '1px solid rgba(13,148,136,0.3)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🧠</span>
                  <span className="text-teal-300 font-mystical font-semibold text-sm">Aurora Lyz — Análise Junguiana</span>
                </div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>
              <button onClick={() => { setAnswer(''); setRelato(''); setPergunta(''); setError(null); }} className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium">
                Nova análise
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default JungPage;

